/**
 * @fileoverview Notification Service for Web App
 * @description Handles notification polling, fetching, and management with 30-second intervals
 */

import { apiClient } from '../lib/api.js';

const POLL_INTERVAL = 30000; // 30 seconds

class NotificationService {
  constructor() {
    this.pollInterval = null;
    this.listeners = new Set();
    this.isPolling = false;
    this.lastFetchTime = null;
  }

  /**
   * Start polling for notifications
   * @param {Function} callback - Callback function to call with new notifications
   */
  startPolling(callback) {
    if (this.isPolling) {
      return; // Already polling
    }

    this.isPolling = true;
    
    // Fetch immediately
    this.fetchNotifications().then(callback).catch(console.error);

    // Then poll every 30 seconds
    this.pollInterval = setInterval(() => {
      this.fetchNotifications()
        .then(callback)
        .catch(error => {
          console.error('Notification polling error:', error);
        });
    }, POLL_INTERVAL);
  }

  /**
   * Stop polling for notifications
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Fetch notifications from API
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Notifications data
   */
  async fetchNotifications(options = {}) {
    try {
      const { page = 1, limit = 20, unreadOnly = false, type = null } = options;
      
      const params = {
        page: page.toString(),
        limit: limit.toString(),
        ...(unreadOnly && { unreadOnly: 'true' }),
        ...(type && { type })
      };

      const response = await apiClient.get('/notifications', { params });
      
      // Handle response structure - backend returns { success: true, data: {...} }
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          this.lastFetchTime = new Date();
          return response.data.data;
        }
        
        // If response.data is the data directly (some APIs might return it differently)
        if (response.data.notifications || response.data.pagination) {
          this.lastFetchTime = new Date();
          return response.data;
        }
        
        // If success is false, throw error
        if (response.data.success === false) {
          throw new Error(response.data.error || 'Failed to fetch notifications');
        }
      }

      throw new Error('Invalid response structure');
    } catch (error) {
      console.error('Fetch notifications error:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      
      if (response.data?.success) {
        return response.data.data?.count || 0;
      }

      return 0;
    } catch (error) {
      console.error('Get unread count error:', error);
      return 0;
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<void>}
   */
  async markAsRead(notificationId) {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Mark as read error:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<number>} Number of notifications marked as read
   */
  async markAllAsRead() {
    try {
      const response = await apiClient.patch('/notifications/mark-all-read');
      
      if (response.data?.success) {
        return response.data.data?.count || 0;
      }

      return 0;
    } catch (error) {
      console.error('Mark all as read error:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise<void>}
   */
  async deleteNotification(notificationId) {
    try {
      await apiClient.delete(`/notifications/${notificationId}`);
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   * @returns {Promise<Object>} User preferences
   */
  async getPreferences() {
    try {
      const response = await apiClient.get('/notifications/preferences');
      
      if (response.data?.success) {
        return response.data.data;
      }

      // Return default preferences if API fails
      return {
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        preferences: {},
        quietHours: {}
      };
    } catch (error) {
      console.error('Get preferences error:', error);
      // Return defaults on error
      return {
        enabled: true,
        pushEnabled: true,
        emailEnabled: false,
        preferences: {},
        quietHours: {}
      };
    }
  }

  /**
   * Update notification preferences
   * @param {Object} preferences - Preference updates
   * @returns {Promise<Object>} Updated preferences
   */
  async updatePreferences(preferences) {
    try {
      const response = await apiClient.put('/notifications/preferences', preferences);
      
      // Handle different response structures
      if (response && response.data) {
        // If response.data is the success object
        if (response.data.success && response.data.data) {
          return response.data.data;
        }
        // If response.data is the data directly (some APIs might return it differently)
        if (response.data.userId || response.data.enabled !== undefined) {
          return response.data;
        }
        // If success is false, throw error
        if (response.data.success === false) {
          throw new Error(response.data.error || 'Failed to update preferences');
        }
      }

      throw new Error('Invalid response structure');
    } catch (error) {
      console.error('Update preferences error:', error);
      console.error('Response:', error.response?.data || error);
      throw error;
    }
  }

  /**
   * Cleanup expired notifications (client-side filtering)
   * @param {Array} notifications - Notifications array
   * @returns {Array} Filtered notifications
   */
  cleanupExpired(notifications) {
    const now = new Date();
    return notifications.filter(notification => {
      if (!notification.expiresAt) return true;
      return new Date(notification.expiresAt) > now;
    });
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
